import { Suspense, useEffect, useRef } from 'react'
import { throttle } from 'lodash'
import LocationGoogleMap from 'google-map-react'

// State
import { useThemeContext } from 'src/context/theme'

// Hooks
import { useListingDetailsLocationMapStyles } from 'src/hooks/listings/useMapStyles'

// Components
import { LineScale } from 'src/Components'
import Marker from '../../../LocationMap/Marker'
import ExitFullScreenButton from './ExitFullScreenButton'

// Styles
import styles from './FullScreenLocationMap.module.scss'

const MAP_WRAPPER_ID = 'full-screen-map-wrapper'

const LoadingContainer = () => (
  <div className={styles.loadingContainer}>
    <LineScale />
  </div>
)

type ListingProps = {
  id: string
  address: { latitude: number; longitude: number }
}

interface FullScreenLocationMapProps {
  listing: ListingProps
}

const FullScreenLocationMap = ({ listing }: FullScreenLocationMapProps) => {
  const { isLightThemeType } = useThemeContext()

  const listingDetailsLocationMapStyles =
    useListingDetailsLocationMapStyles(isLightThemeType)

  const mapRef = useRef(null)

  const calculateVh = () => {
    const el = document.getElementById(MAP_WRAPPER_ID)
    if (el) {
      const height = window.innerHeight - 89
      el.style.height = `${height}px`
    }
  }

  useEffect(() => {
    calculateVh()

    window.addEventListener('resize', throttle(calculateVh, 100))
    window.addEventListener('orientationchange', calculateVh)

    return () => {
      window.removeEventListener('resize', calculateVh)
      window.removeEventListener('orientationchange', calculateVh)
    }
  }, [])

  const {
    id,
    address: { latitude, longitude },
  } = listing || {}

  const center = {
    lat: latitude,
    lng: longitude,
  }

  const zoom = 15

  const mapOptions = maps => ({
    styles: listingDetailsLocationMapStyles,
    clickableIcons: false,
    mapTypeControl: true,
    minZoom: 8,
    maxZoom: 20,
    zoomControlOptions: {
      position: maps.ControlPosition.RIGHT_TOP,
    },
    scrollwheel: true,
    disableDefaultUI: true,
    streetViewControl: true,
    scaleControl: false,
    gestureHandling: 'greedy',
    zoomControl: true,
    fullscreenControl: false,
    rotateControl: false,
  })

  return (
    <div id={MAP_WRAPPER_ID} ref={mapRef} className={styles.wrapper}>
      <Suspense fallback={<LoadingContainer />}>
        <ExitFullScreenButton className={styles.position} listingId={id} />
        <LocationGoogleMap
          yesIWantToUseGoogleMapApiInternals
          bootstrapURLKeys={{
            key: process.env.REACT_APP_GOOGLE_KEY as string,
          }}
          center={center}
          zoom={zoom}
          options={mapOptions}
        >
          {/* @ts-expect-error: the library definition does not handle lat and lng */}
          <Marker lat={center.lat} lng={center.lng} />
        </LocationGoogleMap>
      </Suspense>
    </div>
  )
}

export default FullScreenLocationMap
